import { formatCents, tt } from '@agos/shared'

/**
 * Orders whose printed receipt and stored total disagree - `FRONTEND.md` §9.463, `BACKEND.md` §10A.226.
 *
 * **Why this screen exists at all.** The owner's answer to `OWNER-QUESTIONS.md` #63 was *"it varied by
 * person"*, which took the repair of 439 historical orders off the table: if some waiters charged the
 * figure on the screen and some knew the zľava, no rule separates *this customer was over-charged* from
 * *this row is merely inconsistent*. **So the owed thing is the one that stops it recurring**, and this
 * is it - AGOS holds the printed total and the stored total and had never compared them.
 *
 * **It will be empty for a while and that is correct.** `fiskal_documents` holds nothing on any stack
 * whose receipts predate `V59`, so this fills as documents are printed rather than reporting history.
 * The empty state says so, because a blank panel that looks broken is worse than no panel.
 */

/** One row as `GET /fiskal/total-mismatches` answers it. */
export interface TotalMismatch {
  orderId?: number
  orderNumber?: string
  type?: string
  status?: string
  customerName?: string
  printedCents?: number
  recordedCents?: number
  receiptNumber?: string
  kasaId?: string
  printedAt?: string
}

function cents(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

/**
 * Printed less recorded, in cents.
 *
 * **Signed, and the sign is the whole point.** Positive means the register printed *more* than the
 * order records, so the customer paid more than the row admits. Negative is the other way and the
 * restaurant is short. A screen that showed only a magnitude would make the two indistinguishable,
 * which is the mistake §9.452 records on a different money panel.
 */
export function mismatchDifferenceCents(row: TotalMismatch): number {
  return cents(row.printedCents) - cents(row.recordedCents)
}

/** What the fold's button says, so the count and the money are visible without opening it. */
export function mismatchSummary(rows: TotalMismatch[]): string {
  if (rows.length === 0) return ''
  const worst = rows.reduce(
    (most, row) => Math.max(most, Math.abs(mismatchDifferenceCents(row))),
    0,
  )
  return `${rows.length} · ${tt('najväčší rozdiel')} ${formatCents(worst)}`
}

/**
 * The sentence under an empty panel.
 *
 * Stated rather than left blank: this check has no history to report on a stack older than `V59`, and
 * a reader who does not know that will think it is broken.
 */
export function mismatchEmptyNote(): string {
  /* One literal, not a concatenation: the Slovak-text lint rule reads the argument of `tt()` and
   * cannot see through `+`, which is right - a sentence assembled from pieces is a sentence no
   * translation pass can find. */
  return tt('Žiadne rozdiely. Kontrola porovnáva sumu na doklade so sumou na objednávke a začína platiť od prvého vytlačeného dokladu - staršie doklady sa neporovnávajú.')
}

/** Which way it went, said in words rather than left to a minus sign. */
export function mismatchDirection(row: TotalMismatch): string {
  const difference = mismatchDifferenceCents(row)
  if (difference > 0) return tt('doklad je vyšší ako objednávka')
  if (difference < 0) return tt('objednávka je vyššia ako doklad')
  return tt('rovnaké')
}
